import { useTranslation } from "react-i18next";

import { useTenantBranding, type DeliveryTierFact } from "@/api/queries/branding";
import { pickI18nBlob } from "@/components/admin/I18nField";

const FEATURES = ["price", "geometry", "delivery"] as const;

/**
 * Three-column features block — value props between the step row
 * and the FAQ. Each card has an icon, a tight headline and a one-
 * sentence body. Stays text-only (no image assets) so a copy edit
 * doesn't ripple into the build.
 *
 * The "delivery" card is composed dynamically from
 * ``landing_facts``: title from the fastest active delivery tier,
 * body lists the configured tiers + active shipping carriers (no
 * "DPD, PPL i osobní odběr" hardcode). Falls back to soft static
 * copy on the platform default tenant where ``landing_facts`` is
 * null.
 */
export function Features(): JSX.Element {
  const { t, i18n } = useTranslation("common");
  const branding = useTenantBranding();
  const facts = branding.data?.landing_facts ?? null;

  const renderTitle = (key: (typeof FEATURES)[number]): string => {
    if (key !== "delivery") return t(`home.features.${key}.title`);
    return deliveryTitle(t, facts?.delivery_tiers ?? []);
  };

  const renderBody = (key: (typeof FEATURES)[number]): string => {
    if (key !== "delivery") return t(`home.features.${key}.body`);
    return deliveryBody(
      t,
      facts?.delivery_tiers ?? [],
      facts?.shipping_carrier_names ?? [],
      i18n.language,
    );
  };

  return (
    <div className="st-ben grid gap-6 md:grid-cols-3" data-testid="home-features">
      {FEATURES.map((key) => (
        <div key={key} className="st-ben-i flex flex-col gap-2">
          <span
            aria-hidden="true"
            className="st-bi mono text-2xl text-accent"
          >
            {ICONS[key]}
          </span>
          <h3 className="text-base font-semibold text-ink">
            {renderTitle(key)}
          </h3>
          <p className="text-sm text-ink-2">{renderBody(key)}</p>
        </div>
      ))}
    </div>
  );
}

function deliveryTitle(
  t: ReturnType<typeof useTranslation>["t"],
  tiers: DeliveryTierFact[],
): string {
  // Days of the DELIVERY promise (production + fastest transit), never the
  // tier's production floor sold as delivery (KAN-1803).
  const days = tiers[0]?.delivery_days;
  if (days && days >= 1 && days <= 7) {
    return t("home.features.delivery.titleDays", { count: days });
  }
  return t("home.features.delivery.title");
}

/**
 * Top-of-section heading. Composed from the fastest configured
 * delivery tier so the page never promises a number the tenant
 * hasn't actually set up. Falls back to a generic phrase when no
 * tiers are configured.
 */
export function buildFeaturesHeading(
  t: ReturnType<typeof useTranslation>["t"],
  tiers: DeliveryTierFact[],
): string {
  const deliveryFragment = deliveryFragmentForHeading(t, tiers);
  return t("home.features.headingTemplate", { delivery: deliveryFragment });
}

function deliveryFragmentForHeading(
  t: ReturnType<typeof useTranslation>["t"],
  tiers: DeliveryTierFact[],
): string {
  const days = tiers[0]?.delivery_days;
  if (days && days >= 1 && days <= 7) {
    return t("home.features.headingDeliveryDays", { count: days });
  }
  return t("home.features.headingDeliveryFallback");
}

function deliveryBody(
  t: ReturnType<typeof useTranslation>["t"],
  tiers: DeliveryTierFact[],
  carriers: string[],
  lang: string,
): string {
  // A tier whose delivery time is unknown is left out — better silent than
  // a production floor passed off as delivery (KAN-1803).
  const promised = tiers.filter((tier) => (tier.delivery_days ?? 0) >= 1);
  if (promised.length === 0 && carriers.length === 0) {
    return t("home.features.delivery.bodyFallback");
  }
  const tiersFragment = promised
    .map((tier) => {
      // The tenant's English override when they authored one — the
      // label used to be read as ``label_cz`` on every locale, so an
      // /en visitor got the Czech tier name in an English sentence.
      const label = pickI18nBlob(
        tier.label_i18n,
        tier.label_cz,
        tier.label_en,
        lang,
      );
      return t("home.features.delivery.tierDays", {
        label,
        count: tier.delivery_days ?? 0,
      });
    })
    .join(", ");
  const carriersFragment = joinCarriers(carriers, t);
  if (tiersFragment && carriersFragment) {
    return `${tiersFragment}. ${carriersFragment}.`;
  }
  return tiersFragment ? `${tiersFragment}.` : `${carriersFragment}.`;
}

function joinCarriers(
  carriers: string[],
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (carriers.length === 0) return "";
  if (carriers.length === 1) return carriers[0];
  const head = carriers.slice(0, -1).join(", ");
  const tail = carriers[carriers.length - 1];
  const joiner = t("home.features.delivery.joiner");
  return `${head} ${joiner} ${tail}`;
}

const ICONS: Record<(typeof FEATURES)[number], string> = {
  price: "⚡",
  geometry: "◷",
  delivery: "→",
};
